/** Harta orașului, adusă în cadrul desenului — și drumul invers, pentru
 *  centrul lotului pe care îl cerem serverului.
 *
 *  Serverul dă punctele în Stereo 70, `[nord, est]` — ordinea documentelor pe
 *  EPSG:3844. De aici încolo trec prin `punctInCadru`, adică exact prin
 *  transformarea documentului (`northUp` + originea locală), deci harta cade
 *  peste lot fără nicio întoarcere proprie. Altă grilă decât Stereo 70 nu are
 *  hartă (serverul știe doar România). */

import { punctInCadru } from "../canonical";
import type { Inel, Oras3, RaspunsOras, Suprafata3 } from "./tipuri";

const EPSG_STEREO70 = 3844;

type Crs = { epsg?: number | null } | null | undefined;

export const areHarta = (crs: Crs): boolean => crs?.epsg === EPSG_STEREO70;

/** Un punct din cadrul desenului înapoi în Stereo 70 — inversa lui
 *  `punctInCadru` pe o grilă northing-first: `[est − ox, −nord − oy]`. */
export function inStereo70(crs: Crs, origine: [number, number],
                           p: [number, number]): { nord: number; est: number } | null {
  if (!areHarta(crs)) return null;
  return { est: p[0] + origine[0], nord: -(p[1] + origine[1]) };
}

/** Centrul cerut serverului: mijlocul casetei lotului, rotunjit la metru. Același
 *  pe plan și pe machetă, deci trecerea dintr-o vedere în alta nu cere altă
 *  hartă; o soluție nouă a aceluiași lot dă același centru. */
export function centruLot(lot: readonly number[][] | null | undefined): [number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of lot ?? []) {
    if (!Number.isFinite(p?.[0]) || !Number.isFinite(p?.[1])) continue;
    minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
  }
  if (!Number.isFinite(minX)) return null;
  return [Math.round((minX + maxX) / 2), Math.round((minY + maxY) / 2)];
}

function decodeaza(inel: number[], r: RaspunsOras,
                   inCadru: (p: number[]) => number[]): Inel {
  const out: Inel = [];
  const u = r.unitate_m;
  const [on, oe] = r.origine;
  for (let i = 0; i + 1 < inel.length; i += 2) {
    out.push(inCadru([on + inel[i] * u, oe + inel[i + 1] * u]));
  }
  return out;
}

function inPoligon(x: number, y: number, inel: number[][]): boolean {
  let in_ = false;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const [xi, yi] = inel[i];
    const [xj, yj] = inel[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi) in_ = !in_;
  }
  return in_;
}

/** Atinge clădirea lotul? (centrul sau un colț înăuntru) — atunci e o clădire
 *  care se dărâmă sau chiar a noastră, și nu se desenează peste proiect. */
function peLot(inel: Inel, cx: number, cy: number, lot: number[][]): boolean {
  if (lot.length < 3) return false;
  if (inPoligon(cx, cy, lot)) return true;
  return inel.some((p) => inPoligon(p[0], p[1], lot));
}

/** Răspunsul serverului în cadrul desenului, fără clădirile de pe lot. */
export function orasInCadru(r: RaspunsOras, crs: Crs, origine: [number, number],
                            lot: number[][], centru: [number, number]): Oras3 | null {
  if (!areHarta(crs) || r.epsg !== EPSG_STEREO70) return null;
  const inCadru = punctInCadru(crs, origine);
  const supr = (l: { inele: number[][] }[]): Suprafata3[] =>
    l.map((e) => ({ inele: e.inele.map((i) => decodeaza(i, r, inCadru)) }))
      .filter((s) => s.inele[0]?.length >= 3);
  const cladiri: Oras3["cladiri"] = [];
  for (const c of r.cladiri) {
    const inele = c.inele.map((i) => decodeaza(i, r, inCadru)).filter((i) => i.length >= 3);
    if (!inele.length || !(c.h > 0)) continue;
    let cx = 0, cy = 0;
    for (const p of inele[0]) { cx += p[0]; cy += p[1]; }
    cx /= inele[0].length;
    cy /= inele[0].length;
    if (peLot(inele[0], cx, cy, lot)) continue;
    // un server mai vechi trimite doar `cunoscut`
    const sursa = c.sursa ?? (c.cunoscut ? "osm" : "estimat");
    cladiri.push({ inele, h: c.h, sursa, cx, cy });
  }
  return {
    cladiri,
    strazi: supr(r.strazi.filter((s) => s.fel !== "pietonal")),
    pietonale: supr(r.strazi.filter((s) => s.fel === "pietonal")),
    caiFerate: supr(r.cai_ferate),
    verde: supr(r.verde),
    apa: supr(r.apa),
    centru,
    lot,
    raza: r.raza_m,
    atribuire: r.atribuire,
  };
}
